import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled from 'styled-components';

import { ChevronDown } from '../../icons/Icons';
import { type UseCaseTemplateType } from '../../templates/useCaseTemplate';
import { Text } from '../design-system';

import { Author } from './sharedStyles';

const AuthorWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
`;

const StyledAuthor = styled(Author)`
  opacity: 1;
`;

const AuthorImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 100%;
`;

const ReadButton = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  width: fit-content;
  transition: opacity ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  &:hover {
    opacity: 0.6;
  }
`;

const ChevronUp = styled(ChevronDown)`
  transform: rotate(180deg);
`;

const Line = styled.div`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => rgba(theme.highlightColor, 0.35)};
`;

interface AuthorDescriptionProps {
  useCase: UseCaseTemplateType;
}

export const AuthorDescription: React.ComponentType<AuthorDescriptionProps> =
  React.memo(({ useCase }) => {
    const [displayAllText, setDisplayAllText] = useState<boolean>(false);
    const description: string | undefined = useCase?.description?.description;

    // keep description text full-length
    // when it's 180 chars < description text < 240 chars
    // OR shorter than 180 chars
    // otherwise, truncate the text at 170 chars and add ellipses

    const truncatedText: string = useMemo(() => {
      if (description && description.length) {
        if (description.length < 240 && description.length > 130) {
          return description;
        } else if (description.length >= 240) {
          return `${description.slice(0, 170)}...`;
        }
      }
      return ''; // Handle the case where description is undefined or has no length
    }, [description]);

    const handleClick = useCallback(() => {
      setDisplayAllText(displayAllText => !displayAllText);
    }, []);

    return (
      <>
        {useCase.author && useCase.authorPhoto && (
          <AuthorWrapper>
            <AuthorImage
              alt={useCase.authorPhoto.title}
              src={useCase.authorPhoto.url}
            />
            <StyledAuthor>{useCase.author}</StyledAuthor>
          </AuthorWrapper>
        )}
        <Text size="large">{displayAllText ? description : truncatedText}</Text>
        {description && description.length > truncatedText.length && (
          <>
            <ReadButton onClick={handleClick}>
              {displayAllText ? (
                <>
                  Read less <ChevronUp />
                </>
              ) : (
                <>
                  Read more <ChevronDown />
                </>
              )}
            </ReadButton>
            <Line />
          </>
        )}
      </>
    );
  });
